import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { SnackbarProvider } from "notistack";
import {
  BookkeepingPage,
  CalendarPage,
  ChildAgeAnalysisPage,
  ChildDetailPage,
  CommunicationsPage,
  DailyReportsPage,
  FamiliesPage,
  FamilyDetailPage,
  HomePage,
  ImmunizationsPage,
  InspectionReportPage,
  Layout,
  NotFound,
  TaxReportPage,
  ToolsPage,
} from "pages";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./App.css";
import { useAccessToken } from "./app/auth";
import { useAppDispatch } from "./app/hooks";
import { DialogProvider } from "./components";
import { useVisibilityRefresh } from "./hooks";
import Login from "./pages/Login";
import { api, Tags } from "./redux/apiStore";
import theme from "./theme";

export default function App() {
  const accessToken = useAccessToken();
  const [loginComplete, setLoginComplete] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (loginComplete === !!accessToken) return;
    setTimeout(() => {
      setLoginComplete(true);
    }, 1300);
  }, [accessToken]);

  // Refresh main data when app comes back into focus
  useVisibilityRefresh(() => {
    // Invalidate main tags to force refetch of key data
    dispatch(api.util.invalidateTags([Tags.Family, Tags.Child, Tags.Communication, Tags.DailyReport, Tags.AttendanceLogEntry, Tags.Receipt]));
  }, loginComplete);

  if (!loginComplete) {
    return <Login />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={theme}>
        <SnackbarProvider
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <DialogProvider>
            <CssBaseline />
            <BrowserRouter>
              <Routes>
                <Route path="/print/tax-report" element={<TaxReportPage />} />
                <Route path="/print/inspection-report" element={<InspectionReportPage />} />
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="calendar" element={<CalendarPage />} />
                  <Route path="attendance" element={<Navigate to="/calendar" replace />} />
                  <Route path="/bookkeeping" element={<BookkeepingPage />} />
                  <Route path="/communications" element={<CommunicationsPage />} />
                  <Route path="/daily-reports" element={<DailyReportsPage />} />
                  <Route path="/families" element={<FamiliesPage />} />
                  <Route path="/families/:familyId" element={<FamilyDetailPage />} />
                  <Route path="/families/:familyId/children/:childId" element={<ChildDetailPage />} />
                  <Route path="/immunizations" element={<ImmunizationsPage />} />
                  <Route path="/tools" element={<ToolsPage />} />
                  <Route path="/tools/child-age-analysis" element={<ChildAgeAnalysisPage />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </DialogProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

// import "@fontsource/roboto/300.css";
// import "@fontsource/roboto/400.css";
// import "@fontsource/roboto/500.css";
// import "@fontsource/roboto/700.css";
// import { AppDrawer, DRAWER_WIDTH } from "components";
// import "./App.css";

// import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
// import Box from "@mui/material/Box";
// import CssBaseline from "@mui/material/CssBaseline";
// import Typography from "@mui/material/Typography";
// import { styled } from "@mui/material/styles";
// import * as React from "react";

// const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
//   open?: boolean;
// }>(({ theme, open }) => ({
//   flexGrow: 1,
//   padding: theme.spacing(3),
//   transition: theme.transitions.create("margin", {
//     easing: theme.transitions.easing.sharp,
//     duration: theme.transitions.duration.leavingScreen,
//   }),
//   marginLeft: `-${DRAWER_WIDTH}px`,
//   ...(open && {
//     transition: theme.transitions.create("margin", {
//       easing: theme.transitions.easing.easeOut,
//       duration: theme.transitions.duration.enteringScreen,
//     }),
//     marginLeft: 0,
//   }),
// }));

// interface AppBarProps extends MuiAppBarProps {
//   open?: boolean;
// }

// const AppBar = styled(MuiAppBar, {
//   shouldForwardProp: (prop) => prop !== "open",
// })<AppBarProps>(({ theme, open }) => ({
//   transition: theme.transitions.create(["margin", "width"], {
//     easing: theme.transitions.easing.sharp,
//     duration: theme.transitions.duration.leavingScreen,
//   }),
//   ...(open && {
//     width: `calc(100% - ${DRAWER_WIDTH}px)`,
//     marginLeft: `${DRAWER_WIDTH}px`,
//     transition: theme.transitions.create(["margin", "width"], {
//       easing: theme.transitions.easing.easeOut,
//       duration: theme.transitions.duration.enteringScreen,
//     }),
//   }),
// }));

// export default function App() {
//   const [open, setOpen] = React.useState(true);

//   const handleDrawerOpen = () => {
//     setOpen(true);
//   };

//   const handleDrawerClose = () => {
//     setOpen(false);
//   };

//   return (
//     <Box sx={{ display: "flex" }}>
//       <CssBaseline />
//       <AppDrawer open={open} onOpen={handleDrawerOpen} onClose={handleDrawerClose} />
//       <Main open={open}>
//         {/* <DrawerHeader /> */}
//         <Typography paragraph>
//           Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Rhoncus dolor purus non
//           enim praesent elementum facilisis leo vel. Risus at ultrices mi tempus imperdiet. Semper risus in hendrerit gravida rutrum quisque non tellus.
//           Convallis convallis tellus id interdum velit laoreet id donec ultrices. Odio morbi quis commodo odio aenean sed adipiscing. Amet nisl suscipit
//           adipiscing bibendum est ultricies integer quis. Cursus euismod quis viverra nibh cras. Metus vulputate eu scelerisque felis imperdiet proin fermentum
//           leo. Mauris commodo quis imperdiet massa tincidunt. Cras tincidunt lobortis feugiat vivamus at augue. At augue eget arcu dictum varius duis at
//           consectetur lorem. Velit sed ullamcorper morbi tincidunt. Lorem donec massa sapien faucibus et molestie ac.
//         </Typography>
//         <Typography paragraph>
//           Consequat mauris nunc congue nisi vitae suscipit. Fringilla est ullamcorper eget nulla facilisi etiam dignissim diam. Pulvinar elementum integer enim
//           neque volutpat ac tincidunt. Ornare suspendisse sed nisi lacus sed viverra tellus. Purus sit amet volutpat consequat mauris. Elementum eu facilisis
//           sed odio morbi. Euismod lacinia at quis risus sed vulputate odio. Morbi tincidunt ornare massa eget egestas purus viverra accumsan in. In hendrerit
//           gravida rutrum quisque non tellus orci ac. Pellentesque nec nam aliquam sem et tortor. Habitant morbi tristique senectus et. Adipiscing elit duis
//           tristique sollicitudin nibh sit. Ornare aenean euismod elementum nisi quis eleifend. Commodo viverra maecenas accumsan lacus vel facilisis. Nulla
//           posuere sollicitudin aliquam ultrices sagittis orci a.
//         </Typography>
//       </Main>
//     </Box>
//   );
// }

// // function App() {
// //   return (
// //     <div className="App">
// //       <header className="App-header">
// //         <img src={logo} className="App-logo" alt="logo" />
// //         <Counter />
// //         <p>
// //           Edit <code>src/App.tsx</code> and save to reload.
// //         </p>
// //         <span>
// //           <span>Learn </span>
// //           <a
// //             className="App-link"
// //             href="https://reactjs.org/"
// //             target="_blank"
// //             rel="noopener noreferrer"
// //           >
// //             React
// //           </a>
// //           <span>, </span>
// //           <a
// //             className="App-link"
// //             href="https://redux.js.org/"
// //             target="_blank"
// //             rel="noopener noreferrer"
// //           >
// //             Redux
// //           </a>
// //           <span>, </span>
// //           <a
// //             className="App-link"
// //             href="https://redux-toolkit.js.org/"
// //             target="_blank"
// //             rel="noopener noreferrer"
// //           >
// //             Redux Toolkit
// //           </a>
// //           ,<span> and </span>
// //           <a
// //             className="App-link"
// //             href="https://react-redux.js.org/"
// //             target="_blank"
// //             rel="noopener noreferrer"
// //           >
// //             React Redux
// //           </a>
// //         </span>
// //       </header>
// //     </div>
// //   )
// // }
